// Libraries
import moment from 'moment'
import {
  CognitoUserPool,
  CognitoUser,
  CognitoAccessToken,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoUserSession
} from 'amazon-cognito-identity-js'

// Services
import api from '../../api'
import store from '../index'
import { EventBus } from '../../services/eventBusService.js'

// Utilities
import logger from '../../utility/loggerUtil'
import isEmpty from '../../utility/isEmpty'
import messaging from '../../utility/messagingUtil'
import router from '../../utility/routerUtil'
import session from '../../utility/sessionUtil'
import sessionRecord from '../../utility/sessionRecordUtil'
import wait from '../../utility/waitUtil'

import { phone } from '../../validators/phone'
import { CognitoConstants, KeyConstants } from '../../constants'

export default {
  async authenticate (context, data) {
    try {
      if (!data.isEmailVerification) {
        wait.waitStart('session/authenticate')
      }

      logger.info('Before login attempt in store...')

      let user
      if (data.profileId && (data.flow || data.signature)) {
        let authResponse
        if (data.flow) {
          authResponse = await api.user.deviceKeyAuth(data.flow, data.profileId)
        } else if (data.signature) {
          authResponse = await api.user.signatureAuth(data.profileId, data.signature, data.expiration, data.path, data.isGuestPayment)
        } else {
          throw new Error('No recognized authentication parameters.')
        }
        authResponse = Object.assign(authResponse, { profileId: data.profileId })
        user = await context.dispatch('createSessionFromServerAuth', authResponse)
      } else {
        const usernamePasswordResponse = await api.user.login(data.username, data.password, data.rememberMe, context.state.fromLegacyNcr, context.state.isInvictus)
        user = usernamePasswordResponse.user

        router.clearLockedFlows()

        await api.user.deviceKeySignout().catch(e => { // clear the locked flow on normal login, let them continue if error
          logger.error('Error on device key signout')
          logger.debug(e)
        })
      }

      if (context.rootState.payment.guestCard && context.rootState.payment.saveGuestCard) {
        context.commit('payment/reset')
      }

      const { email, emailVerified, phone, phoneVerified, username } = await getContactInfo(context)

      context.commit('authenticated', { user, username, email, emailVerified, phone, phoneVerified })
      await context.dispatch('onLogin')
    } catch (e) {
      const responseMessage = !isEmpty(e.message) && e.message.toLowerCase().includes('request failed with status code') ? undefined : e.message
      const responseError = !isEmpty(e.response) && !isEmpty(e.response.data) ? e.response.data.error : undefined
      const err = new Error(e.errorCode || responseMessage || responseError)
      err.detail = e.message
      throw err
    } finally {
      if (!data.isEmailVerification) {
        wait.waitEnd('session/authenticate')
      }
    }
  },

  async register (context, data) {
    try {
      let guestCardIds

      if (context.rootState.payment.guestCard && context.rootState.payment.saveGuestCard) {
        guestCardIds = context.rootState.payment.paymentCardIds
      }

      const user = await api.user.register(data.username, data.password, context.state.isInvictus, guestCardIds)
      context.commit('unverified', { user, username: data.username })
    } catch (e) {
      logger.error('Error on registration')
      logger.debug(e)
      throw e
    }
  },

  async verify (context, data) {
    try {
      const user = await api.user.verify(data.username, data.code, data.isEmail)
      context.commit('authenticated', { user, username: data.username })
    } catch (e) {
      logger.error('Error on session/verify')
      logger.debug(e)
      throw e
    }
  },

  async resendVerificationCode (context, data) {
    try {
      await api.user.resendVerificationCode(data.username)
    } catch (e) {
      logger.error('Error on resend verification')
      logger.debug(e)
      throw new Error(e)
    }
  },

  async signoutInvictus (context) {
    try {
      wait.waitStart('session/signout')

      context.commit('setSignOutInProgress', true) // Prevent loop of signout calls when auth tokens expire

      await api.user.signout()
      context.commit('signoutInvictus')
      context.commit('customer/clearCustomerData', null, { root: true })
      context.commit('setSignOutInProgress', false)
    } catch (e) {
      logger.error('Error on signing out invictus')
      logger.debug(e)
      throw e
    } finally {
      wait.waitEnd('session/signout')
    }
  },

  async signout (context, data) {
    try {
      wait.waitStart('session/signout')

      context.commit('setSignOutInProgress', true) // Prevent loop of signout calls when auth tokens expire

      if (!isEmpty(session.getAccessToken())) {
        await api.user.deviceKeySignout().catch(e => { // clear the locked flow on normal login, let them continue if error
          logger.error('Error on device key signout')
          logger.error(e)
        })
      }

      await api.user.signout()
      router.clearLockedFlows()

      context.commit('signout')
      context.commit('customer/clearCustomerData', null, { root: true })

      if (isEmpty(data) || isEmpty(data.skipPaymentReset) || !data.skipPaymentReset) {
        context.commit('payment/clearPaymentData', null, { root: true })
      }

      context.commit('cards/reset', null, { root: true })
      context.commit('config/reset', null, { root: true })
      context.commit('loans/reset', null, { root: true })
      context.commit('appElement/reset', null, { root: true })
      context.commit('store/reset', null, { root: true })
      context.commit('brands/reset', null, { root: true })
      context.commit('referral/reset', null, { root: true })
      context.commit('offers/reset', null, { root: true })
      context.commit('display/closeSidebar', null, { root: true })
      context.commit('origination/reset', undefined, { root: true })
      EventBus.$emit('signout')
      store.cache.clear()

      context.commit('setSignOutInProgress', false)
    } catch (e) {
      throw new Error(e)
    } finally {
      wait.waitEnd('session/signout')
    }
  },

  /**
   * Gets the global service status for maintenance mode
   */
  async getServiceStatus (context) {
    try {
      const status = await api.config.getServiceStatus()
      context.commit('updateServiceStatus', status)
      return status
    } catch (e) {
      logger.error('Error on session/getServiceStatus')
      logger.debug(e)
      throw e
    }
  },

  /**
   * Checks to see if the current device is trusted or not, using the Cognito device key.
   * @return {Promise<Boolean>} true if the device is trusted, false otherwise.
   */
  async checkTrustedDeviceStatus (context, skipWaitIndicator = false) {
    try {
      if (!skipWaitIndicator) {
        wait.waitStart('session/checkTrustedDeviceStatus')
      }

      const deviceKey = await context.dispatch('getCurrentDeviceKey')
        .catch(e => {
          logger.error('Error getting device key')
          logger.debug(e)
        })

      if (isEmpty(deviceKey)) {
        return {
          isTrusted: false
        }
      }

      const isEmail = !phone(context.getters.getUsername)
      const response = await api.user.getTrustedDeviceStatus(deviceKey, isEmail, context.getters.isShortFormSource)

      return {
        isTrusted: response.isTrusted === true,
        contactInfo: response.contactInfo,
        deviceKey,
        hasError: response.hasError
      }
    } catch (e) {
      logger.error('Error checking trusted device')
      logger.debug(e)
      throw e
    } finally {
      if (!skipWaitIndicator) {
        wait.waitEnd('session/checkTrustedDeviceStatus')
      }
    }
  },

  /**
   * Sends a new trusted device code to the Cognito account for a given device.
   */
  async sendTrustedDeviceCode (context, data) {
    const deviceKey = await context.dispatch('getCurrentDeviceKey')
    const isPhone = phone(context.getters.getUsername)
    const isVoice = !isEmpty(data) ? data.isVoice : false

    await api.user.sendTrustedDeviceCode(deviceKey, !isPhone, isVoice, context.getters.isShortFormSource)
  },

  /**
   * Sends a new trusted device code to the Cognito account for a given device.
   */
  async confirmTrustedDeviceCode (context, data) {
    let deviceKey = data.deviceKey

    if (isEmpty(deviceKey)) {
      deviceKey = await context.dispatch('getCurrentDeviceKey')
    }

    await api.user.confirmTrustedDeviceCode(deviceKey, data.code, data.profileId)
  },

  /**
   * Sends a new user update code to the Cognito account for a given alias.
   */
  async confirmUserUpdateCode (context, data) {
    await api.user.confirmUserUpdateCode(data.newAlias, data.code)
  },

  isOverSessionTimeout () {
    const minutesDifference = moment().diff(store.state.session.sessionTimeoutStart, 'minutes')

    logger.debug(`Session timeout length (minutes): ${minutesDifference}`)

    return minutesDifference >= store.state.session.sessionTimeoutLimit
  },

  async getCurrentDeviceKey () {
    let deviceKey = window.localStorage.getItem(KeyConstants.deviceKey)

    if (isEmpty(deviceKey)) {
      deviceKey = session.getDeviceKey() // Issue with Cognito SDK prevents device key from being accessed when doing custom auth on the first login
      if (isEmpty(deviceKey)) {
        deviceKey = (await api.user.getCurrentDevice()).Device.DeviceKey
      }
    }

    if (isEmpty(deviceKey)) { // If it is still empty, throw
      throw new Error('MissingDeviceKey')
    }

    window.localStorage.setItem(KeyConstants.deviceKey, deviceKey)

    return deviceKey
  },

  async setDeviceKey (context, data) {
    logger.debug(`Setting new device key: ${JSON.stringify(data.deviceKey)}`)

    window.localStorage.setItem(KeyConstants.deviceKey, data.deviceKey)
  },

  async createSessionFromServerAuth (context, data) {
    const { profileId, accessToken, deviceKey, deviceGroupKey, idToken, refreshToken } = data

    logger.debug('Refresh client SDK with tokens and device key')

    const cognitoUserPool = new CognitoUserPool({
      UserPoolId: context.state.servicesConfig.userPoolId, // Your user pool id here
      ClientId: context.state.servicesConfig.clientId, // Your client id here
      Storage: window.sessionStorage
    })

    const AccessToken = new CognitoAccessToken({ AccessToken: accessToken })
    const IdToken = new CognitoIdToken({ IdToken: idToken })
    const RefreshToken = new CognitoRefreshToken({ RefreshToken: refreshToken })

    const sessionData = {
      IdToken,
      AccessToken,
      RefreshToken
    }

    const userSession = new CognitoUserSession(sessionData)

    const userData = {
      Username: profileId,
      Pool: cognitoUserPool,
      Storage: window.sessionStorage
    }

    const cognitoUser = new CognitoUser(userData)

    cognitoUser.setSignInUserSession(userSession)

    // This seems to be the only way to fill in these properties when not using the client SDK authenticate method
    cognitoUser.deviceKey = deviceKey
    cognitoUser.deviceGroupKey = deviceGroupKey

    cognitoUser.cacheDeviceKeyAndPassword()
    cognitoUser.getCachedDeviceKeyAndPassword()

    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err, session) => { // You must run this to verify that session (internally)
        if (err) {
          logger.error('Error on get session')
          logger.debug(err)
          reject(err)
        }

        if (session.isValid()) {
          logger.debug(session)
          logger.debug(`access token: ${session.getAccessToken().getJwtToken()}`)
          logger.debug(`id token: ${session.getIdToken().getJwtToken()}`)

          resolve(cognitoUser)
        } else {
          logger.error('Invalid session')
          logger.debug(session)
          reject(new Error('Invalid session')) // TODO: Handle this error appropriately
        }
      })
    })
  },

  async getPasswordPolicy (context) {
    try {
      const passwordPolicy = await api.user.getPasswordPolicy()
      context.commit('setPasswordPolicy', passwordPolicy)
    } catch (e) {
      logger.error('Error getting password policy')
      logger.debug(e)
      throw e
    }
  },

  async getConfigSettings (context) {
    const configSettings = await api.config.getConfigSettings().catch(e => {
      logger.error('Error getting config settings, trying again...')
      logger.debug(e)
      return api.config.getConfigSettings()
    })

    logger.info('Got config settings')
    logger.debug(configSettings)

    context.commit('updateConfigSettings', configSettings.appConfig)
  },

  async onLogin () {
    await messaging.onLogin()
    sessionRecord.updateIdentity()
  },

  async getSecurityQuestions (context) {
    try {
      let { questions } = await api.user.getSecurityQuestions()

      if (!isEmpty(questions)) {
        questions = questions.sort((a, b) => a.displayOrder - b.displayOrder)
      }

      logger.info('Got security questions')
      logger.debug(questions)

      context.commit('saveSecurityQuestions', questions)
    } catch (error) {
      logger.error('Failed to get security questions')
      logger.debug(error)

      throw error
    }
  },

  async saveSecurityQuestionAnswer (context, { questionId, answer }) {
    try {
      await api.user.saveSecurityQuestion(questionId, answer)
    } catch (error) {
      logger.error('Failed to save security question answer')
      logger.debug(error)

      throw error
    }
  },

  async getAnsweredSecurityQuestions (context) {
    try {
      const answeredQuestions = await api.user.getAnsweredSecurityQuestions()

      if (isEmpty(answeredQuestions)) {
        return
      }

      if (answeredQuestions.newCustomer) {
        context.commit('setNewInvictusCustomer', true)
      }

      const answer = (answeredQuestions.questions || [])[0] // Only 1 answer is supported for now

      logger.info('Got answered security question')
      logger.debug(answer)

      context.commit('saveAnsweredSecurityQuestion', answer || {})
    } catch (error) {
      logger.error('Failed to get answered security questions')
      logger.debug(error)

      throw error
    }
  }
}

const getContactInfo = async (context) => {
  let username
  const { attributes } = await context.dispatch('customer/getCustomerId', undefined, { root: true })

  const emailAttribute = attributes.find(attr => attr.getName() === CognitoConstants.emailAttribute)
  const phoneAttribute = attributes.find(attr => attr.getName() === CognitoConstants.phoneNumberAttribute)

  const email = !isEmpty(emailAttribute) ? emailAttribute.getValue() : ''
  const phone = !isEmpty(phoneAttribute) ? phoneAttribute.getValue() : ''

  const emailVerifiedAttribute = attributes.find(attr => attr.getName() === CognitoConstants.emailVerifiedAttribute)
  const emailVerified = !isEmpty(emailVerifiedAttribute) && emailVerifiedAttribute.getValue() === 'true'

  const phoneVerifiedAttribute = attributes.find(attr => attr.getName() === CognitoConstants.phoneNumberVerifiedAttribute)
  const phoneVerified = !isEmpty(phoneVerifiedAttribute) && phoneVerifiedAttribute.getValue() === 'true'

  if (!isEmpty(email) && emailVerified) {
    username = email
  } else if (!isEmpty(phone) && phoneVerified) {
    username = phone
  }

  return {
    email,
    emailVerified,
    phone,
    phoneVerified,
    username
  }
}
